import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Stack, CircularProgress, Alert, Chip, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, alpha, TextField, MenuItem, Select, FormControl,
  InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  useTheme, InputAdornment, Grid, Card, CardContent, Pagination
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { fetchAllDesignTransactions } from '../designApi';

const STATUS_CONFIG = {
  pending: { color: '#ed6c02', icon: <PendingIcon />, label: 'Pending' },
  approved: { color: '#2e7d32', icon: <CheckCircleIcon />, label: 'Approved' },
  rejected: { color: '#d32f2f', icon: <CancelIcon />, label: 'Rejected' },
  completed: { color: '#1565c0', icon: <TaskAltIcon />, label: 'Completed' }
};

// ✅ Helper to safely get nested field values with fallbacks
const getField = (obj, ...fields) => {
  if (!obj) return null;
  for (const field of fields) {
    const parts = field.split('.');
    let val = obj;
    for (const part of parts) {
      val = val?.[part];
      if (val === undefined || val === null) break;
    }
    if (val !== undefined && val !== null) return val;
  }
  return null;
};

// ✅ Helper to safely extract an array from any API response structure
// Backend getAllSubscriptions returns: { subscriptions: [...], pagination: { page, limit, total, totalPages } }
const extractArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.subscriptions)) return data.subscriptions;
  if (Array.isArray(data.transactions)) return data.transactions;
  if (Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.items)) return data.items;
  return [];
};

// ✅ Helper to safely extract pagination info
// Backend returns: { pagination: { page, limit, total, totalPages } }
const extractPagination = (data, fallbackLength = 0) => {
  if (!data || Array.isArray(data)) {
    return {
      totalPages: 1,
      totalCount: Array.isArray(data) ? data.length : fallbackLength,
      currentPage: 1
    };
  }

  // ✅ Check for nested pagination object first (backend standard)
  const pag = data.pagination || data.Pagination;
  if (pag) {
    return {
      totalPages: pag.totalPages || pag.TotalPages || 1,
      totalCount: pag.total || pag.Total || pag.count || fallbackLength,
      currentPage: pag.page || pag.Page || pag.currentPage || 1
    };
  }

  return {
    totalPages: data.totalPages || data.TotalPages || data.pages ||
      Math.ceil((data.total || data.count || fallbackLength) / 20) || 1,
    totalCount: data.total || data.Total || data.count || data.Count ||
      data.totalCount || fallbackLength,
    currentPage: data.page || data.Page || data.currentPage || 1
  };
};

export default function DesignTransactionHistory() {
  const { t } = useTranslation();
  const theme = useTheme();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [receiptDialog, setReceiptDialog] = useState({ open: false, imageUrl: '' });
  const [detailDialog, setDetailDialog] = useState({ open: false, transaction: null });

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      // ✅ Backend getAllSubscriptions accepts query params: ?status=&page=&limit=
      const data = await fetchAllDesignTransactions(statusFilter, page, 20);
      console.log('📦 fetchAllDesignTransactions response:', data);

      const transArray = extractArray(data);
      const pagination = extractPagination(data, transArray.length);

      console.log('📦 Extracted transactions:', transArray.length, 'items, pagination:', pagination);

      setTransactions(transArray);
      setTotalPages(pagination.totalPages);
      setTotalCount(pagination.totalCount);
    } catch (err) {
      console.error('Failed to load transactions:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load transaction history');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // ✅ Safe filtering - transactions is guaranteed to be an array
  const filteredTransactions = Array.isArray(transactions) ? transactions.filter(tr => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    // ✅ Backend subscription format fields:
    // SubscriptionId, AdminId, AdminName, AdminNumber, PlanName, WalletName, WalletNumber
    const subId = String(getField(tr, 'SubscriptionId', 'subscriptionId', 'Id', 'id') || '').toLowerCase();
    const planName = String(getField(tr, 'PlanName', 'planName') || '').toLowerCase();
    const adminName = String(getField(tr, 'AdminName', 'adminName') || '').toLowerCase();
    const adminNumber = String(getField(tr, 'AdminNumber', 'adminNumber') || '').toLowerCase();
    const walletName = String(getField(tr, 'WalletName', 'walletName') || '').toLowerCase();
    const walletNumber = String(getField(tr, 'WalletNumber', 'walletNumber') || '').toLowerCase();
    return (
      subId.includes(query) ||
      planName.includes(query) ||
      adminName.includes(query) ||
      adminNumber.includes(query) ||
      walletName.includes(query) ||
      walletNumber.includes(query)
    );
  }) : [];

  // ✅ Safe status counts from current page data
  // Note: These counts are only for the current page, not total.
  // The totalCount from pagination is the overall total.
  const safeTransactions = Array.isArray(transactions) ? transactions : [];
  const statusCounts = {
    all: totalCount || safeTransactions.length,
    pending: safeTransactions.filter(item => getField(item, 'Status', 'status') === 'pending').length,
    approved: safeTransactions.filter(item => getField(item, 'Status', 'status') === 'approved').length,
    rejected: safeTransactions.filter(item => getField(item, 'Status', 'status') === 'rejected').length,
    completed: safeTransactions.filter(item => getField(item, 'Status', 'status') === 'completed').length
  };

  if (loading && page === 1) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha('#1565c0', 0.08)} 0%, ${alpha('#6a1b9a', 0.08)} 100%)`,
          border: `1px solid ${alpha('#1565c0', 0.15)}`
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: '#1565c0', width: 50, height: 50 }}>
              <HistoryIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="#1565c0">
                {t('SubscriptionHistory') || 'Subscription History'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('SubscriptionHistoryDesc') || 'View all design plan subscription transactions'}
              </Typography>
            </Box>
          </Stack>
          <Tooltip title={t('Refresh') || 'Refresh'}>
            <IconButton onClick={loadTransactions} sx={{ bgcolor: alpha('#1565c0', 0.1) }}>
              <RefreshIcon sx={{ color: '#1565c0' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t('All') || 'All', count: statusCounts.all, color: '#455a64', filterVal: '' },
          { label: t('Pending') || 'Pending', count: statusCounts.pending, color: '#ed6c02', filterVal: 'pending' },
          { label: t('Approved') || 'Approved', count: statusCounts.approved, color: '#2e7d32', filterVal: 'approved' },
          { label: t('Rejected') || 'Rejected', count: statusCounts.rejected, color: '#d32f2f', filterVal: 'rejected' },
          { label: t('Completed') || 'Completed', count: statusCounts.completed, color: '#1565c0', filterVal: 'completed' }
        ].map((stat) => (
          <Grid item xs={6} sm={2.4} key={stat.label}>
            <Card
              elevation={0}
              onClick={() => { setStatusFilter(stat.filterVal); setPage(1); }}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(stat.color, statusFilter === stat.filterVal ? 0.5 : 0.15)}`,
                bgcolor: statusFilter === stat.filterVal ? alpha(stat.color, 0.05) : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: alpha(stat.color, 0.4), bgcolor: alpha(stat.color, 0.03) }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 1.5, px: 1, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5" fontWeight={700} color={stat.color}>{stat.count}</Typography>
                <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Search & Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: `1px solid ${alpha('#000', 0.08)}` }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            placeholder={t('SearchTransactions') || 'Search by ID, plan, admin...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              )
            }}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>{t('Status') || 'Status'}</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              label={t('Status') || 'Status'}
            >
              <MenuItem value="">{t('AllStatuses') || 'All Statuses'}</MenuItem>
              <MenuItem value="pending">{t('Pending') || 'Pending'}</MenuItem>
              <MenuItem value="approved">{t('Approved') || 'Approved'}</MenuItem>
              <MenuItem value="rejected">{t('Rejected') || 'Rejected'}</MenuItem>
              <MenuItem value="completed">{t('Completed') || 'Completed'}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Loading indicator for page changes */}
      {loading && page > 1 && (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={30} />
        </Box>
      )}

      {/* Table */}
      {!loading && filteredTransactions.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: `1px dashed ${alpha('#000', 0.2)}` }}>
          <HistoryIcon sx={{ fontSize: 64, color: alpha('#000', 0.15), mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {searchQuery
              ? (t('NoSearchResults') || 'No transactions match your search')
              : statusFilter
                ? (t('NoTransactionsForStatus') || `No ${statusFilter} transactions found`)
                : (t('NoTransactionsFound') || 'No transactions found')
            }
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery
              ? (t('TryDifferentSearch') || 'Try a different search term')
              : (t('TransactionsWillAppear') || 'Transactions will appear here when admins subscribe to plans')
            }
          </Typography>
        </Paper>
      ) : !loading && (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha('#000', 0.08)}` }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha('#1565c0', 0.05) }}>
                  <TableCell sx={{ fontWeight: 700 }}>{t('SubscriptionID') || 'Subscription ID'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('Admin') || 'Admin'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('Plan') || 'Plan'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">{t('Amount') || 'Amount'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('Wallet') || 'Wallet'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">{t('Status') || 'Status'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('Date') || 'Date'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">{t('Actions') || 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.map((tr, index) => {
                  // ✅ Backend formatSubscriptionResponse returns:
                  // Id, SubscriptionId, AdminId, AdminName, AdminNumber,
                  // PlanId, PlanName, NumberOfDesigns,
                  // Amount, Currency, CurrencySymbol, FormattedAmount,
                  // WalletId, WalletName, WalletNumber,
                  // Status, ReceiptImage, RejectionReason,
                  // UploadedDesignsCount, SentDesignsCount, RemainingUploads,
                  // CreatedAt, UpdatedAt, ApprovedAt
                  const trId = getField(tr, 'Id', 'id');
                  const trSubscriptionId = getField(tr, 'SubscriptionId', 'subscriptionId') || trId || '-';
                  const trStatus = getField(tr, 'Status', 'status') || 'pending';
                  const trAdminName = getField(tr, 'AdminName', 'adminName') || '-';
                  const trAdminNumber = getField(tr, 'AdminNumber', 'adminNumber') || '';
                  const trPlanName = getField(tr, 'PlanName', 'planName') || '-';
                  const trDesigns = getField(tr, 'NumberOfDesigns', 'numberOfDesigns') || 0;
                  // ✅ Use FormattedAmount from backend if available
                  const trFormattedAmount = getField(tr, 'FormattedAmount', 'formattedAmount');
                  const trAmount = getField(tr, 'Amount', 'amount') || 0;
                  const trCurrency = getField(tr, 'Currency', 'currency') || 'YER';
                  const trCurrencySymbol = getField(tr, 'CurrencySymbol', 'currencySymbol') || '';
                  const trWalletName = getField(tr, 'WalletName', 'walletName') || '-';
                  const trWalletNumber = getField(tr, 'WalletNumber', 'walletNumber') || '';
                  const trCreatedAt = getField(tr, 'CreatedAt', 'createdAt');
                  const trReceiptImage = getField(tr, 'ReceiptImage', 'receiptImage');

                  const statusConf = STATUS_CONFIG[trStatus] || STATUS_CONFIG.pending;
                  // ✅ Display formatted amount from backend, with fallback
                  const displayAmount = trFormattedAmount || `${trCurrencySymbol}${trAmount}` || `${trAmount} ${trCurrency}`;

                  return (
                    <TableRow key={trId || index} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                          {trSubscriptionId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: alpha('#6a1b9a', 0.2) }}>
                            {trAdminName.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{trAdminName}</Typography>
                            {trAdminNumber && (
                              <Typography variant="caption" color="text.secondary">{trAdminNumber}</Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{trPlanName}</Typography>
                        <Chip
                          label={`${trDesigns} designs`}
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem', mt: 0.5 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={700} color="#ad1457">
                          {displayAmount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{trWalletName}</Typography>
                        {trWalletNumber && (
                          <Typography variant="caption" color="text.secondary">{trWalletNumber}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={React.cloneElement(statusConf.icon, { sx: { fontSize: 16 } })}
                          label={t(statusConf.label) || statusConf.label}
                          size="small"
                          sx={{
                            bgcolor: alpha(statusConf.color, 0.1),
                            color: statusConf.color,
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{formatDate(trCreatedAt)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {trReceiptImage && (
                            <Tooltip title={t('ViewReceipt') || 'View Receipt'}>
                              <IconButton
                                size="small"
                                onClick={() => setReceiptDialog({ open: true, imageUrl: trReceiptImage })}
                              >
                                <ReceiptIcon fontSize="small" sx={{ color: '#1976d2' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={t('ViewDetails') || 'View Details'}>
                            <IconButton
                              size="small"
                              onClick={() => setDetailDialog({ open: true, transaction: tr })}
                            >
                              <VisibilityIcon fontSize="small" sx={{ color: '#6a1b9a' }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, val) => setPage(val)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Receipt Preview Dialog */}
      <Dialog
        open={receiptDialog.open}
        onClose={() => setReceiptDialog({ open: false, imageUrl: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('PaymentReceipt') || 'Payment Receipt'}
          <IconButton onClick={() => setReceiptDialog({ open: false, imageUrl: '' })}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {receiptDialog.imageUrl ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <img
                src={receiptDialog.imageUrl}
                alt="Payment Receipt"
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'none', mt: 2 }}
              >
                {t('ReceiptLoadError') || 'Failed to load receipt image'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ReceiptIcon sx={{ fontSize: 48, color: alpha('#000', 0.15), mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {t('NoReceiptImage') || 'No receipt image available'}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, transaction: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('TransactionDetails') || 'Transaction Details'}
          <IconButton onClick={() => setDetailDialog({ open: false, transaction: null })}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {detailDialog.transaction && (() => {
            const tr = detailDialog.transaction;
            const trStatus = getField(tr, 'Status', 'status') || 'pending';
            const statusColor = STATUS_CONFIG[trStatus]?.color || '#000';

            // ✅ Backend formatSubscriptionResponse fields
            const trFormattedAmount = getField(tr, 'FormattedAmount', 'formattedAmount');
            const trAmount = getField(tr, 'Amount', 'amount') || 0;
            const trCurrency = getField(tr, 'Currency', 'currency') || 'YER';
            const trCurrencySymbol = getField(tr, 'CurrencySymbol', 'currencySymbol') || '';
            const displayAmount = trFormattedAmount || `${trCurrencySymbol}${trAmount}` || `${trAmount} ${trCurrency}`;
            const trDesigns = getField(tr, 'NumberOfDesigns', 'numberOfDesigns') || 0;
            const trUploaded = getField(tr, 'UploadedDesignsCount', 'uploadedDesignsCount') || 0;
            const trSent = getField(tr, 'SentDesignsCount', 'sentDesignsCount') || 0;
            const trRemaining = getField(tr, 'RemainingUploads', 'remainingUploads');
            const trReceiptImage = getField(tr, 'ReceiptImage', 'receiptImage');

            return (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Paper sx={{ p: 2, bgcolor: alpha('#000', 0.02), borderRadius: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        {t('SubscriptionID') || 'Subscription ID'}
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {getField(tr, 'SubscriptionId', 'subscriptionId') || getField(tr, 'Id', 'id') || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        {t('Status') || 'Status'}
                      </Typography>
                      <Box>
                        <Chip
                          label={trStatus}
                          size="small"
                          sx={{
                            bgcolor: alpha(statusColor, 0.1),
                            color: statusColor,
                            fontWeight: 600,
                            textTransform: 'capitalize'
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        {t('Admin') || 'Admin'}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {getField(tr, 'AdminName', 'adminName') || '-'}
                      </Typography>
                      {getField(tr, 'AdminNumber', 'adminNumber') && (
                        <Typography variant="caption" color="text.secondary">
                          {getField(tr, 'AdminNumber', 'adminNumber')}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        {t('Plan') || 'Plan'}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {getField(tr, 'PlanName', 'planName') || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        {t('Amount') || 'Amount'}
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="#ad1457">
                        {displayAmount}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        {t('Designs') || 'Designs'}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {trDesigns}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        {t('Wallet') || 'Wallet'}
                      </Typography>
                      <Typography variant="body2">
                                                {getField(tr, 'WalletName', 'walletName') || '-'}
                      </Typography>
                      {getField(tr, 'WalletNumber', 'walletNumber') && (
                        <Typography variant="caption" color="text.secondary">
                          {getField(tr, 'WalletNumber', 'walletNumber')}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        {t('CreatedAt') || 'Created At'}
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(getField(tr, 'CreatedAt', 'createdAt'))}
                      </Typography>
                    </Grid>

                    {/* Approved info */}
                    {trStatus === 'approved' && (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            {t('ApprovedAt') || 'Approved At'}
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(getField(tr, 'ApprovedAt', 'approvedAt'))}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {/* Rejected info */}
                    {trStatus === 'rejected' && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          {t('RejectionReason') || 'Rejection Reason'}
                        </Typography>
                        <Typography variant="body2" color="error.main" fontWeight={600}>
                          {getField(tr, 'RejectionReason', 'rejectionReason') || '-'}
                        </Typography>
                      </Grid>
                    )}

                    {/* ✅ FIX: Design counts for approved/completed - removed duplicate SentDesignsCount bug */}
                    {(trStatus === 'approved' || trStatus === 'completed') && (
                      <>
                        <Grid item xs={12}>
                          <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${alpha('#000', 0.08)}` }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                              {t('DesignProgress') || 'Design Progress'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            {t('TotalDesigns') || 'Total'}
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {trDesigns}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            {t('ImagesUploaded') || 'Uploaded'}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} color="#1565c0">
                            {trUploaded} / {trDesigns}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            {t('DesignsSent') || 'Sent Back'}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} color="#2e7d32">
                            {trSent} / {trDesigns}
                          </Typography>
                        </Grid>
                        {trRemaining !== null && trRemaining !== undefined && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {t('RemainingUploads') || 'Remaining Uploads'}
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="#ed6c02">
                              {trRemaining}
                            </Typography>
                          </Grid>
                        )}
                      </>
                    )}

                    {/* Updated At */}
                    {getField(tr, 'UpdatedAt', 'updatedAt') && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('LastUpdated') || 'Last Updated'}
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(getField(tr, 'UpdatedAt', 'updatedAt'))}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>

                {/* Notes section */}
                {getField(tr, 'Notes', 'notes') && (
                  <Paper sx={{ p: 2, bgcolor: alpha('#ed6c02', 0.05), borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('Notes') || 'Notes'}
                    </Typography>
                    <Typography variant="body2">
                      {getField(tr, 'Notes', 'notes')}
                    </Typography>
                  </Paper>
                )}

                {/* Receipt image in detail dialog */}
                {trReceiptImage && (
                  <Paper sx={{ p: 2, bgcolor: alpha('#1565c0', 0.03), borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      {t('PaymentReceipt') || 'Payment Receipt'}
                    </Typography>
                    <Box sx={{ textAlign: 'center' }}>
                      <img
                        src={trReceiptImage}
                        alt="Receipt"
                        style={{
                          maxWidth: '100%',
                          maxHeight: 300,
                          objectFit: 'contain',
                          borderRadius: 8,
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setDetailDialog({ open: false, transaction: null });
                          setReceiptDialog({ open: true, imageUrl: trReceiptImage });
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  </Paper>
                )}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDetailDialog({ open: false, transaction: null })}
            sx={{ textTransform: 'none' }}
          >
            {t('Close') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}